import { Factory } from "@/data/dao/Factory";
import { SystemDao } from "@/data/dao/service/SystemDao";
import { PaymentProvider } from "@/data/model/Enums";
import type { ITransactionCompareDifference, ITransactionCompareResult } from "@/data/model/System";
import type { ITransactionCompareQry } from "@/data/queries/system/TransactionCompareQry";
import { config } from "@/data/util/Config";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";

import { BaseModal } from "@/shared/elements/BaseModal";
import { logError } from "@/shared/logging/errors";

import { i18n } from "@/portal/Trans";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { DatePicker } from "@/portal/formControls/DatePicker";
import { UiNotification } from "@/portal/ui/Notification";

export class TransactionCompareWindow extends BaseModal {
    private $form: JQuery;
    private $submitBtn: JQuery;

    private fromPicker: DatePicker;
    private toPicker: DatePicker;

    constructor() {
        super(i18n("modal.transactionCompare.compareTransactions"), "modal-xl", false);

        this.$element.addClass("trxcompare");

        this.$form = $('<form class="kt-form kt-form--label-right" />').appendTo(this.$body);

        const createSection = $('<div class="kt-section kt-section--first"><div class="kt-section__body" /></div>')
            .appendTo(this.$form)
            .find("> .kt-section__body");
        createSection.append(
            '<div class="row"><label class="col-xl-4"></label><div class="col-lg-9 col-xl-6"><h3 class="kt-section__title kt-section__title-sm">' +
                i18n("modal.transactionCompare.settings") +
                "</h3></div></div>"
        );

        const fromToGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.transactionCompare.dateFromTo") +
                '</label><div class="col-xl-3 from"></div><div class="col-xl-3 to"></div></div>'
        ).appendTo(createSection);
        this.fromPicker = new DatePicker(undefined, { endDate: "today" }).renderTo(
            fromToGroup.find("> .col-xl-3.from")
        );
        this.toPicker = new DatePicker(undefined, { endDate: "today" }).renderTo(fromToGroup.find("> .col-xl-3.to"));

        $('<div class="modal-footer" />').insertAfter(this.$body);

        $('<button type="button" class="btn btn-secondary">' + i18n("general.cancel") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.close();
            });

        this.$submitBtn = $(
            '<button type="button" class="btn btn-success">' +
                i18n("modal.transactionCompare.startComparison") +
                "</button>"
        )
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.$form.submit();
            });

        this.$form.submit((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.onCompare();

            return false;
        });
    }

    private setResult(result: ITransactionCompareResult) {
        const sH = $('<div class="row result" style="text-align:center;" />').appendTo(this.$body.empty());
        sH.append(
            '<div class="col-4"><b>' +
                i18n("modal.transactionCompare.totalCompared") +
                "</b><br />" +
                result.totalCompared +
                "</div>"
        );
        sH.append(
            '<div class="col-4"><b>' +
                i18n("modal.transactionCompare.matches") +
                "</b><br />" +
                result.matched +
                "</div>"
        );
        sH.append(
            '<div class="col-4"><b>' +
                i18n("modal.transactionCompare.differences") +
                "</b><br />" +
                result.differences.length +
                "</div>"
        );

        const tableContainer = $(
            '<div class="kt-datatable kt-datatable--default kt-datatable--brand kt-datatable--loaded"></div>'
        ).appendTo(this.$body);
        const tableConfig: IDataTableConfig<ITransactionCompareDifference> = {
            columnMapping: [
                {
                    name: i18n("modal.transactionCompare.date"),
                    property: "date",
                    sortable: false,
                    styles: { width: "14%" },
                    formatter: (m, p) => {
                        return DateTime.toPreciseDateTimeString(p);
                    },
                },
                {
                    name: i18n("modal.transactionCompare.transaction"),
                    property: "paymentId",
                    sortable: false,
                    styles: { width: "10%" },
                    formatter: (m, p) => {
                        if (p === undefined) return "";

                        return $('<a href="/transaction/' + p + '">Tastier Trx</a>').click((e) => {
                            e.stopPropagation();
                        });
                    },
                },
                {
                    name: i18n("modal.transactionCompare.order"),
                    property: "orderId",
                    sortable: false,
                    styles: { width: "10%" },
                    formatter: (m, p) => {
                        if (p === undefined && m.orderNumber === undefined) return "";

                        if (p === undefined && m.orderNumber !== undefined) return m.orderNumber;

                        return $(
                            '<a href="/order/' + p + '">' + (m.orderNumber !== undefined ? m.orderNumber : p) + "</a>"
                        ).click((e) => {
                            e.stopPropagation();
                        });
                    },
                },
                {
                    name: "Tastier",
                    property: "tastierAmount",
                    sortable: false,
                    styles: { width: "10%" },
                    formatter: (m, p) => {
                        if (p === undefined) return "";

                        return Format.toMoneyFormat(p);
                    },
                },
                {
                    name: "PSP",
                    property: "providerAmount",
                    sortable: false,
                    styles: { width: "10%" },
                    formatter: (m, p) => {
                        if (p === undefined) return "";

                        return Format.toMoneyFormat(p);
                    },
                },
                {
                    name: i18n("modal.transactionCompare.link"),
                    property: "paymentProvider",
                    sortable: false,
                    styles: { width: "23%" },
                    formatter: (m, p) => {
                        if (p === undefined) return "";

                        if (m.providerId !== undefined) {
                            switch (p) {
                                case PaymentProvider.Stripe:
                                    return $(
                                        '<a target="_blank" href="' +
                                            config.stripeUrl +
                                            "payments/" +
                                            m.providerId +
                                            '">Stripe - ' +
                                            m.providerId +
                                            "</a>"
                                    ).click((e) => {
                                        e.stopPropagation();
                                    });
                                case PaymentProvider.Wallee:
                                    return $(
                                        '<a target="_blank" href="' +
                                            (m.merchantId == null || m.merchantId === ""
                                                ? config.walleeUrlOld
                                                : config.walleeUrl + "/" + m.merchantId + "/") +
                                            "payment/transaction/view/" +
                                            m.providerId +
                                            '">Wallee - ' +
                                            m.providerId +
                                            "</a>"
                                    ).click((e) => {
                                        e.stopPropagation();
                                    });
                            }
                        }

                        return i18n("enums.PaymentProvider." + PaymentProvider[p]);
                    },
                },
                {
                    name: i18n("modal.transactionCompare.comment"),
                    property: "comment",
                    sortable: false,
                    styles: { width: "23%" },
                },
            ],
            defaultSortAsc: true,
            locationDependend: false,
            rowsPerPage: 12,
            paginated: true,
        };

        new DataTable<ITransactionCompareDifference>(
            tableContainer,
            {
                getData: (a) => {
                    return new Promise((resolve) => {
                        if (a.limit !== undefined || a.offset !== undefined) {
                            resolve({
                                items: result.differences.slice(a.offset, a.limit! + a.offset!),
                                limit: a.limit!,
                                offset: a.offset!,
                                hasNext: false,
                                hasPrev: false,
                            });
                        } else {
                            resolve({
                                items: result.differences,
                                limit: a.limit!,
                                offset: a.offset!,
                                hasNext: false,
                                hasPrev: false,
                            });
                        }
                    });
                },
            },
            tableConfig
        );
    }

    protected onShow() {}

    protected onHide() {}

    private onCompare() {
        const data: ITransactionCompareQry = {
            rangeFrom: this.fromPicker.getValue(),
            rangeTo: this.toPicker.getValue(),
        };

        let hasError = false;
        if (data.rangeFrom !== undefined && data.rangeTo !== undefined && data.rangeFrom > data.rangeTo) {
            this.fromPicker.setError(true);
            this.toPicker.setError(true);
            hasError = true;
        } else {
            this.fromPicker.setError(false);
            this.toPicker.setError(false);
        }

        if (hasError) return;

        this.$body.html(
            '<div class="generating"><b>' +
                i18n("modal.transactionCompare.comparingHasMade") +
                "</b><br />" +
                i18n("modal.transactionCompare.comparingTransactionDescription") +
                "</div>"
        );
        this.$content.find("> .modal-footer").remove();

        Factory.getInstance<SystemDao>(SystemDao)
            .compareTransactions(data)
            .then((result) => {
                this.setResult(result);
            })
            .catch((ex) => {
                logError(ex);
                this.$body.html(
                    '<div class="generating failed"><b>' +
                        i18n("modal.transactionCompare.comparisonFailed") +
                        "</b><br />" +
                        i18n("modal.transactionCompare.pleaseTryAgain") +
                        "</div>"
                );
                UiNotification.showSuccess(i18n("modal.transactionCompare.transactionsCouldNotBeCompared"));
            });
    }
}
